// P7 (clinic-schedule-page), part A: pure helpers for the Clinic schedule page.
// Patient numbers, names and birth dates go in POST bodies only, never in a URL:
// the day's GET carries only the clinic code, the date and the status scope.

const CODE_RE = /^[A-Za-z0-9]{1,6}$/; // clinic codes are 5 characters (ZZTEST is 6); never a 7-digit patient number
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const ACCESS_URL = "/clinic-schedule/access";
export const SEARCH_URL = "/clinic-schedule/search";

export function dayUrl(location, date, scope) {
  const code = String(location || "").trim().toUpperCase();
  const day = String(date || "").trim();
  const sc = scope === "none" ? "none" : "all";
  if (!CODE_RE.test(code) || !DAY_RE.test(day)) return null;
  return `/clinic-schedule/day?location=${encodeURIComponent(code)}&date=${encodeURIComponent(day)}&scope=${sc}`;
}

// -> { url, body } for the search POST. A name search needs the birth date (the server refuses it too).
export function searchRequest({ first, last, dob, pn, location }) {
  const p = String(pn || "").trim();
  const body = p
    ? { pn: p }
    : { first: String(first || "").trim(), last: String(last || "").trim(), dob: String(dob || "").trim() };
  if (location) body.location = String(location).trim().toUpperCase();
  return { url: SEARCH_URL, body };
}

export function searchProblem({ first, last, dob, pn }) {
  const p = String(pn || "").trim();
  if (p) return /^\d{7}$/.test(p) ? null : "A patient number is 7 digits.";
  if (!String(first || "").trim() || !String(last || "").trim())
    return "Enter the patient's first and last name, or a patient number.";
  if (!String(dob || "").trim()) return "Add the patient's birth date to search by name.";
  return null;
}

// Cancelled and removed visits are hidden unless "Show cancelled" is ticked.
export function visibleVisits(visits, showCancelled) {
  return (visits || []).filter((v) => showCancelled || !v.hidden_by_default);
}

export function showStatusBanner(day) {
  return Boolean(day) && ((day.status_error_patients || 0) > 0 || (day.status_capped_patients || 0) > 0);
}

export function bannerText(day) {
  const parts = [];
  if ((day?.status_error_patients || 0) > 0)
    parts.push(`${day.status_error_patients} patient(s) couldn't be checked with Raintree`);
  if ((day?.status_capped_patients || 0) > 0)
    parts.push(`${day.status_capped_patients} patient(s) weren't checked (too many at once)`);
  return parts.length ? `${parts.join("; ")}. Their visits show "Not confirmed with Raintree". Refresh to try again.` : "";
}

// The marker drawn on a visit (decision 4): amber "Paperwork to sign", amber "Use paper",
// a small check for complete; nothing otherwise (no card or no envelope is never "missing").
export function markerView(marker) {
  if (!marker || !marker.kind) return null;
  if (marker.kind === "to_sign") return { text: "Paperwork to sign", tone: "amber" };
  if (marker.kind === "use_paper") return { text: "Use paper", tone: "amber" };
  if (marker.kind === "complete") return { text: "✓", tone: "check", title: "Paperwork complete" };
  return null;
}

// "YYYY-MM-DDTHH:MM(:SS)" clinic wall clock -> minutes after midnight (no time-zone conversion).
export function wallMinutes(stamp) {
  const m = /T(\d{2}):(\d{2})/.exec(String(stamp || ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

export function wallTimeLabel(stamp) {
  const mins = wallMinutes(stamp);
  if (mins === null) return "";
  const h = Math.floor(mins / 60);
  const mm = String(mins % 60).padStart(2, "0");
  return `${((h + 11) % 12) + 1}:${mm} ${h < 12 ? "AM" : "PM"}`;
}

// The grid's hour range: 7 AM to 7 PM, widened to fit anything on the day.
export function dayRange(day) {
  let lo = 7 * 60;
  let hi = 19 * 60;
  for (const x of [...(day?.visits || []), ...(day?.blocks || [])]) {
    const s = wallMinutes(x.start);
    const e = wallMinutes(x.end);
    if (s !== null) lo = Math.min(lo, Math.floor(s / 60) * 60);
    if (e !== null) hi = Math.max(hi, Math.ceil(e / 60) * 60);
  }
  return { lo, hi };
}

// Overlapping visits sit side by side (calendar lanes). A cluster is a run of visits joined by
// overlaps; each visit takes the first lane free at its start, and every visit in the cluster
// shares the cluster's lane count. An A-B-C chain (A overlaps B, B overlaps C, A doesn't overlap C)
// uses two lanes; a visit that overlaps nothing keeps the full width (lanes = 1).
export function layoutLanes(visits) {
  const items = (visits || [])
    .map((v) => {
      const s = wallMinutes(v.start);
      const e = wallMinutes(v.end);
      return { v, s, e: e !== null && s !== null && e > s ? e : (s ?? 0) + 15 };
    })
    .filter((x) => x.s !== null)
    .sort((a, b) => a.s - b.s || a.e - b.e);
  const out = [];
  let cluster = [];
  let laneEnds = [];
  let clusterEnd = -1;
  const flush = () => {
    for (const c of cluster) out.push({ ...c.v, lane: c.lane, lanes: laneEnds.length });
    cluster = [];
    laneEnds = [];
  };
  for (const it of items) {
    if (cluster.length && it.s >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= it.s);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.e);
    } else {
      laneEnds[lane] = it.e;
    }
    cluster.push({ v: it.v, lane });
    clusterEnd = Math.max(clusterEnd, it.e);
  }
  if (cluster.length) flush();
  return out;
}

// A column is wide enough for two lanes; beyond that it widens (the grid scrolls sideways)
// instead of shrinking boxes below a readable width.
export const BASE_COLUMN_PX = 200;
export const MIN_LANE_PX = 120; // most first + last names fit one line
export function columnWidth(visitsWithLanes) {
  const most = Math.max(1, ...(visitsWithLanes || []).map((v) => v.lanes || 1));
  return Math.max(BASE_COLUMN_PX, most * MIN_LANE_PX);
}

// Columns = therapists; blocks and visits placed by wall-clock minutes, visits in lanes.
// ── open RVNP / RVRA holds (N6 v1.4; view only, never bookable) ─────────────
export const HOLD_LABELS = { new_patient: "New patient hold", readmit: "Re-admit hold" };

// Each hold is one 15-minute slot. Back-to-back slots of the same therapist and category merge into
// one band; a gap, another therapist or the other category starts a new band.
export function mergeHolds(holds) {
  const sorted = (holds || [])
    .filter((h) => h && HOLD_LABELS[h.category] && wallMinutes(h.start) !== null && wallMinutes(h.end) !== null)
    .slice()
    .sort((a, b) => String(a.provider).localeCompare(String(b.provider)) || String(a.category).localeCompare(String(b.category))
      || wallMinutes(a.start) - wallMinutes(b.start));
  const out = [];
  for (const h of sorted) {
    const last = out[out.length - 1];
    if (last && last.provider === h.provider && last.category === h.category && wallMinutes(last.end) === wallMinutes(h.start)) {
      last.end = h.end;
      last.slots += 1;
    } else {
      out.push({ provider: h.provider, category: h.category, start: h.start, end: h.end, slots: 1,
        label: HOLD_LABELS[h.category] });
    }
  }
  return out;
}

export const HOLD_TODO = { key: "holds", label: "Open holds" };
export function holdsIncluded(day) {
  return Boolean(day?.holds_included);
}
export function holdCount(day) {
  return holdsIncluded(day) ? mergeHolds(day.holds).length : 0;
}
// A band dims under any to-do filter except "Open holds".
export function holdDimmed(filter) {
  return Boolean(filter) && filter !== HOLD_TODO.key;
}
export function showHoldsNote(day) {
  return holdsIncluded(day) && Boolean(day?.holds_partial);
}

export function columns(day, showCancelled) {
  const cols = (day?.providers || []).map((p) => ({ ...p, blocks: [], visits: [], holds: [] }));
  const byCode = Object.fromEntries(cols.map((c) => [c.code, c]));
  const col = (code) => {
    if (!byCode[code]) {
      byCode[code] = { code, name: code || "Unassigned", blocks: [], visits: [], holds: [] };
      cols.push(byCode[code]);
    }
    return byCode[code];
  };
  for (const b of day?.blocks || []) col(b.provider).blocks.push(b);
  for (const v of visibleVisits(day?.visits, showCancelled)) col(v.provider).visits.push(v);
  if (holdsIncluded(day)) for (const h of mergeHolds(day.holds)) col(h.provider).holds.push(h);
  for (const c of cols) {
    c.visits = layoutLanes(c.visits);
    c.width = columnWidth(c.visits);
  }
  return cols.filter((c) => c.blocks.length || c.visits.length || c.holds.length || (day?.providers || []).some((p) => p.code === c.code));
}

// ── rebuild 4: a real scheduler grid ────────────────────────────────────────
// Status is the block's colour (fill + a 3 px left bar), never text inside the block. Flags are
// small icons with tooltips. Every block also carries a screen-reader label in words, so colour is
// never the only cue. One element per visit, placed from start, end and therapist column (P38 can
// attach drag later without a rewrite).

export const PX_PER_MIN = 1.6;          // an hour = 96 px, 45 min = 72 px, 15 min = 24 px
export const SHORT_MINUTES = 30;        // under 30 minutes: one line

// Inline hex (Tailwind's palette), so the colours never depend on class scanning: a fill in the
// status's light colour (100 shade), a 3 px bar on the left in its strong colour, and no card border
// except the dashed amber one for "Not confirmed".
export const STATUS_STYLES = {
  scheduled: { words: "Scheduled", fill: "#dbeafe", bar: "#2563eb" },                         // blue-100 / blue-600
  not_confirmed: { words: "Not confirmed with Raintree", fill: "#fef3c7", bar: "#f59e0b", dashed: "#fbbf24" }, // amber
  checked_in: { words: "Checked in", fill: "#d1fae5", bar: "#059669" },                       // emerald-100 / 600
  completed: { words: "Completed", fill: "#e2e8f0", bar: "#64748b" },                         // slate-200 / 500
  no_show: { words: "No-show", fill: "#fee2e2", bar: "#dc2626" },                             // red-100 / 600
  cancelled: { words: "Cancelled", fill: "#f1f5f9", bar: "#94a3b8", struck: true },          // slate-100 / 400
  removed: { words: "Removed from the schedule", fill: "#f1f5f9", bar: "#94a3b8", struck: true },
  not_recorded: { words: "No outcome recorded", fill: "#e2e8f0", bar: "#64748b" },
  checking: { words: "Status still loading", fill: "#eff6ff", bar: "#93c5fd" },               // blue-50 / 300
  unknown: { words: "Unknown status", fill: "#f8fafc", bar: "#94a3b8" },                      // slate-50 / 400: neutral
};

// The block's inline style for a status key: fill, 3 px left bar, no other border unless dashed.
export function blockStyle(key) {
  const st = STATUS_STYLES[key] || STATUS_STYLES.scheduled;
  const side = st.dashed ? `1px dashed ${st.dashed}` : "none";
  return { background: st.fill, borderTop: side, borderRight: side, borderBottom: side, borderLeft: `3px solid ${st.bar}` };
}

export const LEGEND = ["scheduled", "not_confirmed", "checked_in", "completed", "no_show"];
export const LEGEND_SHORT = { not_confirmed: "Not confirmed" };

export function statusKey(v) {
  if (v?.unconfirmed) return "not_confirmed";
  const st = String(v?.status || "");
  if (st === "booked" || st === "unchecked") return "checking";
  return STATUS_STYLES[st] && st !== "unknown" ? st : "unknown";
}

// An unknown status draws neutral (never as Scheduled, never blank) and keeps n8n's own label.
export function statusStyle(v) {
  const key = statusKey(v);
  const st = { key, ...STATUS_STYLES[key] };
  if (key === "unknown") st.words = String(v?.status_label || v?.status || "").trim() || STATUS_STYLES.unknown.words;
  return st;
}

export function minutesOf(v) {
  const s = wallMinutes(v?.start);
  const e = wallMinutes(v?.end);
  return s !== null && e !== null && e > s ? e - s : 15;
}

export function isShort(v) {
  return minutesOf(v) < SHORT_MINUTES;
}

// "9:00 AM" -> "9:00" (the grid's hour labels carry AM / PM).
export function timeShort(stamp) {
  return wallTimeLabel(stamp).replace(/ (AM|PM)$/, "");
}

// PTTX -> "PT tx", OTEVA -> "OT eval", PTREV -> "PT re-eval", PTRA -> "PT re-adm"; else the label.
export function shortType(type, label) {
  const t = String(type || "").toUpperCase();
  if (t === "RVNP") return "New pt";
  if (t === "RVRA") return "Re-admit";
  const m = /^([A-Z]{2})(TX|EVA|REV|RA)$/.exec(t);
  if (m) return `${m[1]} ${{ TX: "tx", EVA: "eval", REV: "re-eval", RA: "re-adm" }[m[2]]}`;
  return label || t || "";
}

// The name, fitted to the rendered width: the full name if it fits; else first name + last
// initial ("Casey P."); "…" only if that still doesn't fit. `measure(text)` returns pixels (the page
// measures with a canvas in the block's real font; tests pass a fixed stand-in).
export function fitName(name, availPx, measure) {
  const m = measure || fallbackMeasure;
  const full = String(name || "").trim().replace(/\s+/g, " ");
  if (!full) return "—";
  if (m(full) <= availPx) return full;
  const parts = full.split(" ");
  const base = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : full;
  if (parts.length > 1 && m(base) <= availPx) return base;
  for (let n = base.length - 1; n >= 1; n -= 1) {
    const t = `${base.slice(0, n).trimEnd()}…`;
    if (m(t) <= availPx) return t;
  }
  return `${base[0]}…`;
}

// Name font: 13 px, medium. About 7.5 px a character when no canvas is available.
export const NAME_FONT_PX = 13;
export function fallbackMeasure(text) {
  return String(text || "").length * 7.5;
}

// A canvas measurer for the block's name font (browser only; falls back to the estimate).
export function makeMeasure(fontFamily) {
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return fallbackMeasure;
    ctx.font = `500 ${NAME_FONT_PX}px ${fontFamily || "sans-serif"}`;
    return (text) => ctx.measureText(String(text || "")).width;
  } catch (e) {
    return fallbackMeasure;
  }
}

// The pixels the name may use: the box less the 3 px bar, 8 px padding each side, and (one-line
// boxes) 16 px per flag icon.
export const BLOCK_PAD_X = 8;
export function namePx(boxWidthPx, iconCount, oneLine) {
  return Math.max(0, boxWidthPx - 3 - 2 * BLOCK_PAD_X - (oneLine ? iconCount * 16 : 0));
}

// The flag icons, in this order. Complete paperwork shows nothing in the block (the panel says it).
export function flags(v) {
  const out = [];
  if (v?.marker?.kind === "to_sign")
    out.push({ key: "to_sign", icon: "Signature", tone: "text-amber-600", words: "Paperwork to sign" });
  if (v?.marker?.kind === "use_paper")
    out.push({ key: "use_paper", icon: "FileWarning", tone: "text-amber-600",
      words: "Use paper: the signing link expired or was voided" });
  const l = lienView(v?.lien);
  if (l) out.push({ key: "lien", icon: "FileCheck", tone: "text-emerald-600", words: l.title });
  if (v && !v.has_card) out.push({ key: "no_card", icon: "UserX", tone: "text-slate-400", words: "No WestStar card" });
  return out;
}

// Screen-reader label: name, time, type, status and flags in words.
export function visitLabel(v, providerName) {
  const parts = [v?.name || "Unnamed patient",
    `${wallTimeLabel(v?.start)} to ${wallTimeLabel(v?.end)}`, v?.type_label || v?.type || "",
    providerName ? `with ${providerName}` : "", statusStyle(v).words, ...flags(v).map((f) => f.words)];
  return parts.filter(Boolean).join(", ");
}

// The to-do strip: counts from the same day data (visible visits), and the dim filter.
export const TODOS = [
  { key: "to_sign", label: "Paperwork to sign" },
  { key: "not_confirmed", label: "Not confirmed" },
  { key: "no_card", label: "No card" },
];
export function matchesTodo(v, key) {
  if (key === HOLD_TODO.key) return false;           // "Open holds" dims every visit
  if (key === "to_sign") return v?.marker?.kind === "to_sign";
  if (key === "not_confirmed") return Boolean(v?.unconfirmed);
  if (key === "no_card") return Boolean(v) && !v.has_card;
  return true;
}
export function todoCounts(visits) {
  const out = {};
  for (const t of TODOS) out[t.key] = (visits || []).filter((v) => matchesTodo(v, t.key)).length;
  return out;
}
export function dimmed(v, filter) {
  return Boolean(filter) && !matchesTodo(v, filter);
}

// Keys: Enter / Space open a focused block (native <button>); Escape closes the panel.
export function keyAction(key) {
  if (key === "Escape" || key === "Esc") return "close";
  if (key === "Enter" || key === " ") return "open";
  return null;
}

// A visit's box inside its column, in px (lane from layoutLanes).
export function laneBox(v, colWidth) {
  const lanes = v?.lanes || 1;
  const w = colWidth / lanes;
  return { left: Math.round((v?.lane || 0) * w) + 2, width: Math.round(w) - 4 };
}

// The lien chip: shown only when the patient has e-signed the lien. Never "not signed".
export function lienView(lien) {
  if (!lien || !lien.signed) return null;
  return {
    text: "Lien signed",
    tone: "green",
    title: lien.attorney_signed
      ? "Patient signed the lien. The attorney has signed too."
      : "Patient signed the lien. The attorney hasn't signed yet.",
  };
}

export function errorText(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return fallback;
}
